import type RootStore from "stores/RootStore";
import type ServiceStore from "stores/ServiceStore";
import { RouteType } from "types/RouteType";

/**
 * Finishing Module Route — B3 from the 2026-06-08 client meeting.
 *
 * Daily checklist of pieces in their finishing phase, organized into four
 * tabs (F1, F2, F3, Past Due WIP). The active tab is driven off a date
 * picker that defaults to today. F1/F2/F3 read the composite
 * `production-schedule/chips` endpoint and filter to phase='finishing' +
 * dayNumber=1|2|3 for that date. Past Due WIP joins panels (by qcStatus
 * IN white/yellow/orange/PrePourCompleted) against the chip set so that
 * panels with no chip on this date (or completed work behind schedule)
 * surface in a single bulk-reject grid.
 *
 * Three stores are warmed on entry: `panelsStore` (so the Past Due WIP
 * grid renders without an empty-flash), `projectsStore` (used for the
 * project name labels in the F1/F2/F3 groupings), and
 * `qcInspectionsStore` (so the QC Report dialog has a hot cache for the
 * most recent inspection per panel). Chip data is fetched directly from
 * the composite endpoint inside Finishing.Body — not preloaded here —
 * because it's tightly coupled to the user-chosen date.
 */
const FinishingRoute: RouteType = {
  name: "Finishing",
  path: "/finishing",
  title: "Finishing",
  layout: "/app",
  group: "Production",
  requiresAuth: true,
  requiresAdmin: false,
  requiresPermission: false,
  viewportCard: {
    header: {
      component: () => import("./Finishing.Page"),
      title: "Finishing",
      enableRefreshButton: true,
      enableBackButton: true,
    },
    body: {
      component: () => import("./Finishing.Body"),
      grayBackground: true,
    },
  },
  beforeEnter: async ({ rootStore }: { rootStore: RootStore }) => {
    try {
      const panelsStore = (
        rootStore as unknown as Record<string, ServiceStore | undefined>
      ).panelsStore;
      const projectsStore = (
        rootStore as unknown as Record<string, ServiceStore | undefined>
      ).projectsStore;
      const qcInspectionsStore = (
        rootStore as unknown as Record<string, ServiceStore | undefined>
      ).qcInspectionsStore;

      await Promise.allSettled([
        panelsStore?.find?.({ query: { $limit: 1000 } }),
        projectsStore?.find?.({ query: { $limit: 500 } }),
        qcInspectionsStore?.find?.({
          // qcInspections query schema doesn't expose createdAt — use the
          // domain-meaningful inspectionDatetime instead (allowed at line ~141
          // of qc-inspections.schema.ts).
          query: { $limit: 200, $sort: { inspectionDatetime: -1 } },
        }),
      ]);
    } catch (error) {
      console.warn("[FinishingRoute] warm-load failed:", error);
    }
    return true;
  },
};

export default FinishingRoute;
